import {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  type ReactNode,
  type HTMLAttributes,
  type ButtonHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

export interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
  className?: string;
  /**
   * Referme le menu des qu'un element est clique dedans (menu d'actions
   * classique). Par defaut le menu reste ouvert, pour les contenus qui
   * s'utilisent en place (cases a cocher, filtres).
   */
  closeOnClick?: boolean;
}

/** min-w-[180px] du menu : sert d'ancrage pour `align="right"` (inchange). */
const MENU_MIN_WIDTH = 180;
/** Ecart entre le declencheur et le menu (equivalent `mt-2`). */
const MENU_GAP = 8;
/** Marge minimale entre le menu et les bords de l'ecran. */
const VIEWPORT_MARGIN = 8;

export interface DropdownPosition {
  top: number;
  left: number;
  /** Pose seulement quand le menu ne tient en hauteur ni dessous ni dessus. */
  maxHeight?: number;
}

/**
 * Position du menu (coordonnees `fixed`). Meme ancrage qu'avant ; ensuite,
 * seulement si le menu sortirait de l'ecran :
 *   - horizontalement, il est decale pour rester a 8 px des bords ;
 *   - verticalement, il s'ouvre au-dessus du declencheur si la place y est,
 *     sinon il prend le cote le plus grand et defile (`maxHeight`).
 * Quand le menu tient (cas courant sur bureau), le resultat est identique a
 * l'ancien calcul.
 */
export function computeDropdownPosition(
  trigger: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right'>,
  menu: { width: number; height: number } | null,
  align: 'left' | 'right',
  viewport: { width: number; height: number }
): DropdownPosition {
  let left = align === 'right' ? trigger.right - MENU_MIN_WIDTH : trigger.left;
  let top = trigger.bottom + MENU_GAP;
  if (!menu) return { top, left };

  const maxLeft = viewport.width - menu.width - VIEWPORT_MARGIN;
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

  const bottomLimit = viewport.height - VIEWPORT_MARGIN;
  if (top + menu.height <= bottomLimit) return { top, left };

  const spaceAbove = trigger.top - MENU_GAP - VIEWPORT_MARGIN;
  if (menu.height <= spaceAbove) {
    return { top: trigger.top - MENU_GAP - menu.height, left };
  }
  const spaceBelow = bottomLimit - top;
  if (spaceAbove > spaceBelow) {
    top = VIEWPORT_MARGIN;
    return { top, left, maxHeight: spaceAbove };
  }
  return { top, left, maxHeight: Math.max(spaceBelow, 0) };
}

const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  ({ trigger, children, align = 'right', className, closeOnClick = false }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const updatePosition = useCallback(() => {
      if (!triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const menu = menuRef.current;
      // `scrollHeight` : hauteur du contenu entier, meme si un `maxHeight`
      // precedent le fait deja defiler (+2 px de bordure).
      const size = menu
        ? { width: menu.offsetWidth, height: menu.scrollHeight + 2 }
        : null;

      setPosition(
        computeDropdownPosition(rect, size, align, {
          width: window.innerWidth,
          height: window.innerHeight,
        })
      );
    }, [align]);

    // A l'ouverture, le menu n'est pas encore monte quand on calcule la
    // position : on la recalcule avant l'affichage, une fois sa taille connue.
    useLayoutEffect(() => {
      if (isOpen) updatePosition();
    }, [isOpen, updatePosition]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          triggerRef.current &&
          !triggerRef.current.contains(target) &&
          menuRef.current &&
          !menuRef.current.contains(target)
        ) {
          setIsOpen(false);
        }
      };

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [isOpen, updatePosition]);

    const handleToggle = () => {
      if (!isOpen) {
        updatePosition();
      }
      setIsOpen(!isOpen);
    };

    return (
      <div ref={ref} className={cn('relative inline-block', className)}>
        <div
          ref={triggerRef}
          onClick={handleToggle}
          className="cursor-pointer"
          role="button"
          aria-haspopup="menu"
          aria-expanded={isOpen}
        >
          {trigger}
        </div>

        {isOpen &&
          createPortal(
            <div
              ref={menuRef}
              className={cn(
                'fixed z-[70] min-w-[180px] rounded-lg border border-neutral-200 bg-white py-1 shadow-lg',
                // Largeur de son contenu quelle que soit sa position (sinon
                // un menu pres du bord droit se tasserait), plafonnee a
                // l'ecran moins les marges.
                'w-max max-w-[calc(100vw-1rem)]',
                'animate-in fade-in-0 zoom-in-95'
              )}
              style={{
                top: position.top,
                left: position.left,
                ...(position.maxHeight !== undefined && {
                  maxHeight: position.maxHeight,
                  overflowY: 'auto' as const,
                }),
              }}
              role="menu"
              onClick={closeOnClick ? () => setIsOpen(false) : undefined}
            >
              {children}
            </div>,
            document.body
          )}
      </div>
    );
  }
);

Dropdown.displayName = 'Dropdown';

// DropdownItem
export interface DropdownItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
}

const DropdownItem = forwardRef<HTMLButtonElement, DropdownItemProps>(
  ({ className, children, icon, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-dark-500',
          'hover:bg-neutral-100 focus-visible:bg-neutral-100',
          'focus-visible:outline-none transition-colors',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        role="menuitem"
        {...props}
      >
        {icon && <span className="h-4 w-4 text-neutral-500">{icon}</span>}
        {children}
      </button>
    );
  }
);

DropdownItem.displayName = 'DropdownItem';

// DropdownDivider
export interface DropdownDividerProps extends HTMLAttributes<HTMLDivElement> {}

const DropdownDivider = forwardRef<HTMLDivElement, DropdownDividerProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('my-1 h-px bg-neutral-200', className)}
        role="separator"
        {...props}
      />
    );
  }
);

DropdownDivider.displayName = 'DropdownDivider';

export { Dropdown, DropdownItem, DropdownDivider };
